// Copyright (c) 2021 embyt GmbH. All rights reserved.
// Author: Roman Morawek

export enum AlarmState {
  ACTIVE = 0, // active, non-acknowledged
  ACK_PENDING = 1, // non-active, non-acknowledged
  ACKD_ACTIVE = 2, // active, acknowledged
  ACKD = 3, // non-active, acknowledged
}

// also defined in mqttmessages.ts, dbtypes.ts
export enum AlarmSource {
  // alarms triggered by controller
  TargetValue = "NVAlarm_Targetvalue",
  Restart = "NVAlarm_Restart",
  MissingSync = "NVAlarm_MissingSync",
  Modul = "NVAlarm_Modul",
  ManualActive = "NVAlarm_ManualActive",
  // only used in Loytec devices:
  EnergyMeter = "NVAlarm_EnergyMeter",
  Linx = "NVAlarm_LINX",
}

export interface IAlarmEntry {
  xaid: string;
  source: AlarmSource;
  alarmTime: number; // ms
  clearTime: number | null;
  value: number;
  state: AlarmState;
  description?: string | null;
}

export interface ILogInfo {
  firstSeqNum: number;
  recCnt: number;
  totalCnt: number;
  firstDateTime: number;
  lastDateTime: number;
  itemNames: {
    [index: number]: string;
  };
}

// engine trend data set
export interface TrendData {
  regperiod: number;
  energy: number; // this is no energy, but the period power!
  costs: number;
  tariff: number;
  energyOffRel?: number;
  maxDisabled?: number;
  pMax?: number;
  trendPMax?: number;
  targetEnergy?: number;
  energymeter?: number;
}

export interface IOData {
  bus: number;
  module: number;
  channel: number;
  value: number;
}

export interface AlarmData {
  alarm_source: AlarmSource;
  state: AlarmState;
  alarm_timestamp: number;
  clear_timestamp: number | null;
  ack_timestamp: number | null;
  ack_source: string | null;
  value: number;
  description: string | null;
}

export interface ILogDataTrend {
  timestamp: number; // s, with fractional ms
  tlId: number;
  data: TrendData;
}

export interface ILogDataIo {
  timestamp: number; // s, with fractional ms
  data: IOData;
}

export enum PermissionFlags {
  ALARMING = 1,
  PARAMETER_READ = 2,
  TRENDING = 4,
  PARAMETER_WRITE = 8,
  WEB_INSTALL = 16,
  SW_BRANCHES = 32,
}

export enum EnlineStatusFlags {
  INIT_DONE = 1,
  DEVICE_INIT_DONE = 2,
  PARAMETER_LOADED = 4,
  REGULATION_RUNNING = 8,
  PARAMETER_DOWNLOAD_AVAILABLE = 16,
}

export enum LogHandle {
  TARIFFLINE = "TariffLine",
  REGULATION = "Regulation",
  ALARM = "Alarm",
  OUTPUT = "Output",
  INPUT = "Input",
}

export interface IReadItemRequest {
  type: "input" | "output" | "energymeter";
  id: number;
}
export interface IWriteRequest {
  ItemName: string;
  Value: string | number;
}
export interface IWriteItemRequest {
  type: "input" | "output";
  id: number;
  value: number | boolean;
}

export interface ILogInfoRequest {
  LogHandle: LogHandle;
  StartDateTime?: number; // epoch, potentially with fractional seconds
}

export interface ILogReadRequest {
  LogHandle: LogHandle;
  StartDateTime?: number;
  NumItems: number;
}
export type ITrendLogReadResponse = {
  timestamp: number;
  tlId: number;
  data: TrendData;
};
export type IAlarmLogReadResponse = {
  timestamp: number;
  data: Omit<AlarmData, "alarm_timestamp">;
};
export type IIoLogReadResponse = {
  timestamp: number;
  data: IOData;
};
export type ILogReadResponse = (
  | ITrendLogReadResponse
  | IAlarmLogReadResponse
  | IIoLogReadResponse
)[];

export interface ICommunicationRequest {
  GetStatus?: boolean;
  GetDeviceInfo?: boolean;
  Read?: string[];
  ReadItem?: IReadItemRequest[];
  Write?: IWriteRequest[];
  WriteItem?: IWriteItemRequest[];
  LogInfo?: ILogInfoRequest[];
  LogRead?: ILogReadRequest;
  Options?: {
    ClientRequestHandle?: string | string[];
  };
}

export interface IDeviceInfoResponse {
  serial: string;
  unlocked?: number;
}
export type IReadResponse = string | undefined;
export type IReadItemResponse =
  | {
      value: number | boolean | undefined;
      power?: number;
      operatingMode?: number;
    }
  | undefined;

export interface ICommunicationResponse {
  Request: ICommunicationRequest;
  GetStatusResponse?: string;
  GetDeviceInfoResponse?: IDeviceInfoResponse;
  ReadResponse?: IReadResponse[];
  ReadItemResponse?: IReadItemResponse[];
  WriteResponse?: boolean[];
  WriteItemResponse?: boolean[];
  LogInfoResponse?: number[];
  LogReadResponse?: ILogReadResponse;
}
